import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface FeatureNotAvailablePageProps {
  featureName?: string;
  title?: string;
  description?: string;
  showReturnHome?: boolean;
  customActions?: React.ReactNode;
}

/**
 * Page component shown when a feature is disabled
 */
export function FeatureNotAvailablePage({
  featureName,
  title,
  description,
  showReturnHome = true,
  customActions
}: FeatureNotAvailablePageProps) {
  const location = useLocation();
  const { refresh, isLoading } = useFeatureFlag();

  const defaultTitle = featureName ? `${featureName} Not Available` : 'Feature Not Available';
  const defaultDescription = featureName 
    ? `The ${featureName} feature is currently disabled. Please check back later or contact support if you believe this is an error.`
    : 'This feature is currently not available. Please check back later.';

  const handleRefresh = async () => {
    await refresh();
    // Note: In a real app, you might want to reload the page or navigate
    // but for testing purposes, we'll just refresh the flags
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teddy via-white to-teddy/30 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-urban/10 to-urban/20 rounded-full flex items-center justify-center">
            <span className="text-4xl text-urban/60">🚫</span>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-urban/10">
          <h1 className="text-2xl font-bold text-urban mb-4">
            {title || defaultTitle}
          </h1>
          
          <p className="text-urban/70 mb-6 leading-relaxed">
            {description || defaultDescription}
          </p>

          {/* Actions */}
          <div className="space-y-3">
            {customActions || (
              <>
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="w-full bg-cardboard hover:bg-cardboard-600 disabled:bg-cardboard/50 text-white font-semibold py-3 px-6 rounded-full transition-all duration-200 hover:shadow-md active:scale-95 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking...
                    </span>
                  ) : (
                    'Check Again'
                  )}
                </button>

                {showReturnHome && (
                  <Link
                    to="/"
                    className="block w-full bg-white hover:bg-teddy text-urban font-semibold py-3 px-6 rounded-full border-2 border-urban/20 hover:border-cardboard/50 transition-all duration-200 hover:shadow-md active:scale-95"
                  >
                    Return to Home
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Debug info in development */}
          {import.meta.env.DEV && (
            <div className="mt-6 p-4 bg-urban/5 rounded-lg text-left">
              <h3 className="text-sm font-semibold text-urban mb-2">Debug Info</h3>
              <div className="text-xs text-urban/70 space-y-1">
                <div>Current Path: {location.pathname}</div>
                {featureName && <div>Feature: {featureName}</div>}
                <div>Environment: {import.meta.env.MODE}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Higher-order component that wraps a route component with feature flag protection
 */
export function withFeatureProtection<P extends object>(
  Component: React.ComponentType<P>,
  featureFlag: string,
  featureName?: string
) {
  const ProtectedComponent = (props: P) => {
    const { isEnabled } = useFeatureFlag();
    
    if (!isEnabled(featureFlag as any)) {
      return (
        <FeatureNotAvailablePage 
          featureName={featureName || featureFlag.replace(/_/g, ' ').toLowerCase()}
        />
      );
    }

    return <Component {...props} />;
  };

  ProtectedComponent.displayName = `withFeatureProtection(${Component.displayName || Component.name})`;
  
  return ProtectedComponent;
}